import { IkeFieldType } from '@ikegps/core-api';
import {
	CollectionModel,
	type CollectionModelBase,
	type CollectionModelFieldSubForm,
	type CollectionModelFieldSubFormInstance,
	FormModelFieldSubForm,
} from '@ikegps/core-shared';
import { ImageButton } from '../../components/button/image.button';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { TopologicalEditorCollectionEditCtrl } from '../../page/topological.editor.page/topological.editor.collection.edit.ctrl';
import { classNames } from '../../util/classnames';
import type { ICollectionViewController } from './collection.view.ctrl.interface';
import { CollectionEditViewField } from './collection.view.field';
import { CSS } from './collection.view.style';
import { SubformDuplicateTool } from './subform.tool/subform.duplicate.tool';
import { SubformRemoveTool } from './subform.tool/subform.remove.tool';

export class CollectionEditViewSubform {
	static view(ctrl: ICollectionViewController, collection: CollectionModelBase): MithrilVNode {
		let showTrueSizeRedDotFieldId: string | null = null;
		if (collection instanceof CollectionModel) {
			const rootTrueSizeFields = collection.schema.fields.filter(
				(f) => !FormModelFieldSubForm.isSubForm(f) && f.type === IkeFieldType.TRUE_SIZE
			);
			const anyRootTrueSizeHasValue = rootTrueSizeFields.some((f) => collection.getField(f.id).hasValue());
			showTrueSizeRedDotFieldId =
				!anyRootTrueSizeHasValue && rootTrueSizeFields.length > 0 ? rootTrueSizeFields[0]!.id : null;
		}

		const realFields: MithrilVNode[] = [];
		const subForms: MithrilVNode[] = [];
		for (const field of collection.schema.fields) {
			if (FormModelFieldSubForm.isSubForm(field)) {
				subForms.push(CollectionEditViewSubform.viewSubform(ctrl, collection, field));
			} else {
				realFields.push(CollectionEditViewField.view(ctrl, collection, field, showTrueSizeRedDotFieldId === field.id));
			}
		}

		const className = `${CSS.form.container} ${CSS.form.isRoot}`;
		const id = `i-${collection.instanceId}`;
		const style = {
			display: TopologicalEditorCollectionEditCtrl.is(ctrl) && ctrl.isCarouselShown === true ? 'none' : 'block',
		};
		return m('div', { className, id, style }, realFields.concat(subForms));
	}

	static viewSubform(
		ctrl: ICollectionViewController,
		collection: CollectionModelBase,
		schema: FormModelFieldSubForm
	): MithrilVNode {
		const id = `i-${collection.instanceId}-${schema.id}`;
		const field = collection.getField<CollectionModelFieldSubForm>(schema.id);
		const instances = field.value || [];
		const isEmpty = instances.length === 0 ? CSS.isEmpty : '';

		let showFooter = false;
		const children: MithrilVNode[] = [];
		for (const instance of instances) {
			children.push(CollectionEditViewSubform.viewInstance(ctrl, instance));
			showFooter = showFooter || ctrl.isExpanded(instance.instanceId);
		}

		const title = schema.displayName;
		const userIsAdmin =
			Services.UserProfile.isDepartmentAdmin(Services.UserProfile.get(Services.User.current.objectId)) ||
			Services.UserProfile.isDepartmentDataAdmin(Services.UserProfile.get(Services.User.current.objectId));
		const userCanEditField = userIsAdmin || !field.schema.adminOnly;

		return m(
			'div',
			{
				className: classNames(CSS.form.container, isEmpty),
				id,
			},
			[
				m('div', { className: CSS.form.header }, [
					m('div', { className: CSS.form.title }, [
						m('div', { className: CSS.form.titleName, title }, title),
						instances.length > 0 ? m('div', { className: CSS.form.titleCount }, String(instances.length)) : null,
					]),
					ctrl.isEditing && userCanEditField ? CollectionEditViewSubform.viewEditTools(ctrl, collection, schema) : null,
				]),
				...children,
				ctrl.isEditing && showFooter && userCanEditField
					? CollectionEditViewSubform.viewFooter(ctrl, collection, schema)
					: null,
			]
		);
	}

	private static viewInstance(
		ctrl: ICollectionViewController,
		collection: CollectionModelFieldSubFormInstance
	): MithrilVNode {
		const id = `i-${collection.instanceId}`;

		const instanceTitle = collection.title;
		const formTitle = collection.formTitle;
		let title = formTitle;
		if (instanceTitle != null && instanceTitle !== formTitle) {
			title = `${formTitle} — ${instanceTitle}`;
		}

		const isExpanded = ctrl.isExpanded(collection.instanceId);

		const spanColor = ctrl.useColorCoding ? FormModelFieldSubForm.getSpanColor(collection) : 'none';

		return m(
			'div',
			{
				id,
				className: classNames(CSS.instance.container, {
					[CSS.instance.isOpen]: isExpanded,
				}),
			},
			[
				m(
					'div',
					{
						title,
						onclick: (e) => ctrl.toggleExpanded(e, collection.instanceId),
						className: CSS.instance.header,
						style: { 'border-left': `6px solid ${spanColor}` },
					},
					[
						m('div', { className: CSS.instance.text }, title),
						CollectionEditViewSubform.viewInstanceEditTools(ctrl, collection),
					]
				),
				isExpanded ? CollectionEditViewSubform.view(ctrl, collection) : null,
			]
		);
	}

	private static viewEditTools(
		ctrl: ICollectionViewController,
		collection: CollectionModelBase,
		schema: FormModelFieldSubForm
	): MithrilVNode {
		const field = collection.getField<CollectionModelFieldSubForm>(schema.id);
		const title = schema.displayName;

		const tools: MithrilVNode[] = [];

		if (Services.SubFormLink.isSubFormCompatible(schema)) {
			tools.push(
				ImageButton.view(MaterialIcon.SPAN_LINK, {
					title: T.SPAN_LINK_TITLE,
					onclick: () => ctrl.beginSpanLink(collection),
					testId: 'subform-span-link',
				})
			);
		}

		tools.push(
			ImageButton.view(MaterialIcon.ADD, {
				title: T.COLLECTION_EDIT_CREATE_SUBFORM,
				titleAttrs: { title },
				onclick: () => field.Mutator.create(),
				testId: 'subform-add',
			})
		);

		return m('div', { className: CSS.form.tools }, tools);
	}

	private static viewFooter(
		ctrl: ICollectionViewController,
		collection: CollectionModelBase,
		schema: FormModelFieldSubForm
	): MithrilVNode {
		const field = collection.getField<CollectionModelFieldSubForm>(schema.id);

		const buttons: MithrilVNode[] = [
			m(
				'button',
				{
					className: CSS.form.footerButton + ' mdl-button',
					'data-testid': 'subform-add',
					onclick: () => field.Mutator.create(),
				},
				[Icon.view(MaterialIcon.ADD), m('span', `Add new ${schema.displayName}`)]
			),
		];

		if (Services.SubFormLink.isSubFormCompatible(schema)) {
			buttons.push(
				m(
					'button',
					{
						className: CSS.form.footerButton + ' mdl-button',
						'data-testid': 'subform-span-link',
						onclick: () => ctrl.beginSpanLink(collection),
					},
					[Icon.view(MaterialIcon.SPAN_LINK), m('span', Translate(T.SPAN_LINK_TITLE))]
				)
			);
		}

		return m('div', { className: CSS.form.footer }, buttons);
	}

	private static viewInstanceEditTools(
		ctrl: ICollectionViewController,
		collection: CollectionModelFieldSubFormInstance
	): MithrilVNode {
		const userIsAdmin =
			Services.UserProfile.isDepartmentAdmin(Services.UserProfile.get(Services.User.current.objectId)) ||
			Services.UserProfile.isDepartmentDataAdmin(Services.UserProfile.get(Services.User.current.objectId));
		const userCanEditField = userIsAdmin || !collection.schema.adminOnly;

		if (!(ctrl.isEditing && userCanEditField)) {
			return null;
		}

		return m('div', { className: CSS.instance.tools }, [
			SubformDuplicateTool.view(collection),
			SubformRemoveTool.view(ctrl, collection),
		]);
	}
}
